import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCustomizationStore } from '../store/customizationStore';
import { useLocalizationStore } from '../store/localizationStore';
import { useTranslation } from 'react-i18next';
import { 
  Filter, 
  Search, 
  X, 
  Check,
  Globe,
  Euro,
  Ruler,
  Settings2,
  ChevronDown
} from 'lucide-react';

const filterCategories = [
  {
    id: 'colors',
    label: 'Color',
    items: [
      { id: 'black', name: 'Black', color: 'bg-gray-900' },
      { 
        id: 'white', 
        name: 'White',
        style: {
          background: 'white',
          boxShadow: 'inset 0 0 0 1px rgba(0, 0, 0, 0.1)',
          border: '2px solid #E5DED3'
        }
      },
      { id: 'grey', name: 'Grey', color: 'bg-gray-400' },
      { 
        id: 'ivory', 
        name: 'Ivory',
        style: {
          background: '#FFFFF0',
          boxShadow: 'inset 0 0 0 1px rgba(0, 0, 0, 0.1)',
          border: '2px solid #E5DED3'
        }
      }
    ]
  },
  {
    id: 'finishes',
    label: 'Finish',
    items: [
      { id: 'matte', name: 'Matte' },
      { id: 'glossy', name: 'Glossy' }
    ]
  },
  {
    id: 'prints',
    label: 'Print Look',
    items: [
      { id: 'marble', name: 'Marble' },
      { id: 'concrete', name: 'Concrete' },
      { id: 'terrazzo', name: 'Terrazzo' },
      { id: 'solid', name: 'Solid' },
      { id: 'wood', name: 'Wood' },
      { id: 'stone', name: 'Stone' }
    ]
  }
];

const languages = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' }
];

const currencies = [
  { code: 'EUR', symbol: '€', name: 'Euro', label: 'EUR - Euro' },
  { code: 'USD', symbol: '$', name: 'US Dollar', label: 'USD - US Dollar' },
  { code: 'GBP', symbol: '£', name: 'British Pound', label: 'GBP - British Pound' }
];

const containerVariants = {
  hidden: { opacity: 0, y: -20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.3,
      ease: "easeOut"
    }
  },
  exit: { 
    opacity: 0, 
    y: -20,
    transition: {
      duration: 0.2
    }
  }
};

export const MaterialFilters: React.FC = () => {
  const { t } = useTranslation();
  const { materialFilters, setMaterialFilters, clearMaterialFilters } = useCustomizationStore();
  const { 
    language, 
    currency,
    setLanguage,
    setCurrency
  } = useLocalizationStore();
  
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [showSettings, setShowSettings] = React.useState(false);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setMaterialFilters({ search: value });
  };

  const toggleFilter = (type: string, value: string) => {
    setMaterialFilters({ [type]: materialFilters[type] === value ? undefined : value });
  };

  const getActiveFiltersCount = () => {
    return Object.values(materialFilters).filter(value => value !== undefined && value !== '').length;
  };

  return (
    <div className="bg-[#1C1C1C] rounded-xl shadow-lg mb-8 overflow-hidden border border-gold/30">
      {/* Top Bar */}
      <div className="p-4 flex items-center gap-4 border-b border-gold/10">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gold/60 w-5 h-5" />
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Search materials..."
            className="w-full pl-10 pr-4 py-3 bg-white/5 border border-gold/20 rounded-xl focus:ring-2 focus:ring-gold focus:border-transparent placeholder-gold/40 text-white"
          />
        </div>

        <div className="flex items-center gap-2">
          {/* Settings Button */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowSettings(!showSettings)}
            className="px-4 py-3 bg-white/5 rounded-xl text-gold hover:bg-white/10 border border-gold/20"
          >
            <Settings2 className="w-5 h-5" />
          </motion.button>

          {/* Filter Button */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-2 px-4 py-3 bg-gold rounded-xl text-white hover:bg-gold-dark transition-colors border border-gold/50"
          >
            <Filter className="w-5 h-5" />
            <span className="font-medium">Filters</span>
            {getActiveFiltersCount() > 0 && (
              <span className="bg-white text-gold text-xs px-2 py-1 rounded-full font-medium">
                {getActiveFiltersCount()}
              </span>
            )}
            <ChevronDown 
              className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            />
          </motion.button>

          {getActiveFiltersCount() > 0 && (
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={clearMaterialFilters}
              className="px-4 py-3 text-gold hover:text-gold-dark bg-white/5 rounded-xl border border-gold/20"
            >
              <X className="w-5 h-5" />
            </motion.button>
          )}
        </div>
      </div>

      {/* Settings Panel */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="border-b border-gold/10 overflow-hidden"
          >
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6 bg-white/5">
              {/* Language Selection */}
              <div>
                <h3 className="text-sm font-medium text-gold mb-3 flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  Language
                </h3>
                <div className="grid grid-cols-1 gap-2">
                  {languages.map(lang => (
                    <motion.button
                      key={lang.code}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setLanguage(lang.code as any)}
                      className={`px-4 py-2 rounded-lg text-sm flex items-center ${
                        language === lang.code
                          ? 'bg-gold/20 text-gold font-medium border border-gold/30'
                          : 'bg-white/5 text-white hover:bg-white/10 border border-gold/10'
                      }`}
                    >
                      <span className="text-base w-8">{lang.flag}</span>
                      <span className="flex-1 text-left">{lang.name}</span>
                      {language === lang.code && (
                        <Check className="w-4 h-4 ml-2" />
                      )}
                    </motion.button>
                  ))}
                </div>
              </div>

              {/* Currency Selection */}
              <div>
                <h3 className="text-sm font-medium text-gold mb-3 flex items-center gap-2">
                  <Euro className="w-4 h-4" />
                  Currency
                </h3>
                <div className="grid grid-cols-1 gap-2">
                  {currencies.map(curr => (
                    <motion.button
                      key={curr.code}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setCurrency(curr.code)}
                      className={`px-4 py-2 rounded-lg text-sm flex items-center justify-between ${
                        currency === curr.code
                          ? 'bg-gold/20 text-gold font-medium border border-gold/30'
                          : 'bg-white/5 text-white hover:bg-white/10 border border-gold/10'
                      }`}
                    >
                      <span className="flex-1 text-left">{curr.label}</span>
                      <span className="font-medium ml-4">{curr.symbol}</span>
                      {currency === curr.code && (
                        <Check className="w-4 h-4 ml-2" />
                      )}
                    </motion.button>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Filter Categories */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="overflow-hidden"
          >
            <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
              {filterCategories.map(category => (
                <div key={category.id}>
                  <h3 className="text-sm font-medium text-gold mb-3">
                    {category.label}
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    {category.items.map(item => (
                      <motion.button
                        key={item.id}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => toggleFilter(category.id.slice(0, -1), item.id)}
                        className={`relative px-3 py-2 rounded-lg text-sm flex items-center gap-2 ${
                          materialFilters[category.id.slice(0, -1)] === item.id
                            ? 'bg-gold/20 text-gold border border-gold/30'
                            : 'bg-white/5 text-white hover:bg-white/10 border border-gold/10'
                        }`}
                      >
                        {'color' in item && (
                          <div className="flex items-center gap-2 flex-1">
                            <span 
                              className={`w-8 h-8 rounded-full ${item.color || ''}`}
                              style={('style' in item) ? item.style : undefined}
                            />
                            <span className="flex-1 text-left">{item.name}</span>
                          </div>
                        )}
                        {!('color' in item) && item.name}
                        {materialFilters[category.id.slice(0, -1)] === item.id && (
                          <Check className="w-4 h-4 absolute right-2" />
                        )}
                      </motion.button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};